import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import {
  AdminRole,
  AdminUser,
  adminUsersApi,
} from '../../../api/adminUsersApi';
import { RbacRole, rolesV2Api } from '../../../api/rolesV2Api';
import { TenantGroup, tenantGroupsApi } from '../../../api/tenantGroupsApi';
import { colors, tabsSx, typo } from '../../../theme/tokens';

import UsersTableV2 from './components/UsersTableV2';
import RolePersonasPanel from './components/RolePersonasPanel';
import GroupsTable from './components/GroupsTable';
import AccessSyncPanel from './sync/AccessSyncPanel';

type TabKey = 'users' | 'roles' | 'groups' | 'sync';

/**
 * Users admin page — RBAC_V2 variant (4 tabs: Users | Roles | Tenant
 * Groups | Access Sync, design §8 + CPT-1135). Rendered by
 * `UsersAdminPage`'s flag switch only when `rbacV2Enabled` — the legacy
 * 2-tab page (`UsersAdminPageLegacy`) is untouched and still used flag-off.
 */
const UsersAdminPageV2: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initial: TabKey =
    tabParam === 'roles' ? 'roles' : tabParam === 'groups' ? 'groups' : tabParam === 'sync' ? 'sync' : 'users';
  const [tab, setTab] = useState<TabKey>(initial);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });

  const usersQuery = useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    queryFn: () => adminUsersApi.listUsers(),
  });
  // Legacy role catalog — still needed for AddUserInlineRow's invite dropdown.
  const rolesQuery = useQuery<AdminRole[]>({
    queryKey: ['admin', 'roles'],
    queryFn: () => adminUsersApi.listRoles(),
  });
  const rolesV2Query = useQuery<RbacRole[]>({
    queryKey: ['rbac-v2', 'roles'],
    queryFn: () => rolesV2Api.listRoles(),
  });
  const groupsQuery = useQuery<TenantGroup[]>({
    queryKey: ['tenant-groups'],
    queryFn: () => tenantGroupsApi.listGroups(),
  });

  const handleTab = (next: TabKey) => {
    setTab(next);
    const nsp = new URLSearchParams(searchParams);
    if (next === 'users') nsp.delete('tab');
    else nsp.set('tab', next);
    setSearchParams(nsp, { replace: true });
  };

  const notify = useMemo(
    () => (msg: string, sev: 'success' | 'error' | 'info') =>
      setSnackbar({ open: true, msg, sev }),
    [],
  );

  const loading = usersQuery.isLoading || rolesQuery.isLoading || rolesV2Query.isLoading;
  const error = usersQuery.isError || rolesQuery.isError;
  const users = usersQuery.data ?? [];
  const roles = rolesQuery.data ?? [];
  const rolesV2 = rolesV2Query.data ?? [];
  const groups = groupsQuery.data ?? [];

  return (
    <Box sx={{ maxWidth: 1200 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: colors.textTertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            mb: 0.5,
          }}
        >
          Admin
        </Typography>
        <Typography sx={{ ...typo.pageTitle, mb: 0.5 }}>Users</Typography>
        <Typography sx={{ ...typo.pageSubtitle }}>
          Manage platform access. Invites land immediately; Auth0 identity binds on the
          user&apos;s first login.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => handleTab(v)}
        sx={tabsSx}
        aria-label="Users, Roles, Tenant Groups"
      >
        <Tab value="users" label={`Users (${users.length})`} />
        <Tab value="roles" label={`Roles (${rolesV2.length})`} />
        <Tab value="groups" label={`Tenant Groups (${groups.length})`} />
        <Tab value="sync" label="Access Sync" />
      </Tabs>

      {/* Self-contained tabs (own query + loading/error state) are dispatched
          before the shared users/roles fetch gate. */}
      {tab === 'sync' ? (
        <AccessSyncPanel onNotify={notify} />
      ) : tab === 'groups' ? (
        <GroupsTable
          groups={groups}
          loading={groupsQuery.isLoading}
          error={groupsQuery.isError}
          onNotify={notify}
        />
      ) : tab === 'roles' ? (
        <RolePersonasPanel onNotify={notify} />
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: colors.adminAccent }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: '8px' }}>
          Failed to load users / roles — try again.
        </Alert>
      ) : (
        <UsersTableV2 users={users} roles={roles} rolesV2={rolesV2} onNotify={notify} />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersAdminPageV2;
